import React, { useCallback, useMemo, useRef, useState, useEffect } from 'react'
import clsx from 'clsx'
import styled from 'styled-components'
import { rem } from 'polished'
import { useIntl } from 'react-intl'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import Group from 'components/utils/Group'
import LineCrownIcon from 'LuxKit/Icons/line/LineCrownIcon'
import LineChairWithAirplaneIcon from 'LuxKit/Icons/line/LineChairWithAirplaneIcon'
import LineTaxiIcon from 'LuxKit/Icons/line/LineTaxiIcon'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import LineShieldCheckIcon from 'LuxKit/Icons/line/LineShieldCheckIcon'
import IconButton from 'LuxKit/Button/IconButton'
import TextButton from 'LuxKit/Button/TextButton'
import getLoyaltyTierDisplayName from 'loyalty/selectors/getLoyaltyTierDisplayName'
import useLoyaltyConfig from 'loyalty/hooks/useLoyaltyConfig'
import eases from 'styles/tools/eases'
import zIndex from 'styles/tools/z-index'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import LoyaltyTierProgressionNotificationMobile from './LoyaltyTierProgressionNotificationMobile'
import { mediaQueryUp } from 'components/utils/breakpoint'
import FormatDateTime from 'components/utils/Formatters/FormatDateTime'
import Plural from 'components/utils/Formatters/Plural'
import { tierGradientBackgroundStyle, tierNormalContrastColorStyle, tierDarkBackgroundStyle, tierLightContrastColorStyle } from 'styles/utils/tierStyles'
import { tierProgressionNotificationMessages } from './LoyaltyTierProgressionNotificationMessages'

const messages = {
  ...tierProgressionNotificationMessages,
  toUnlockSuffix: { id: 'LTPN3Unlk', defaultMessage: 'to unlock these {tierName} benefits:', description: 'Suffix after requirements in tier progression notification' },
  by: { id: 'LTPN4By', defaultMessage: ' by ', description: 'Conjunction before expiry date in tier progression notification' },
  benefitUpgrade: { id: 'LTPN9Upgrade', defaultMessage: '{isUnlimited, select, true {Unlimited} other {{count}x}} free room or package {count, plural, one {upgrade} other {upgrades}}', description: 'Room/package upgrade benefit in tier progression notification' },
  benefitLounge: { id: 'LTPN9Lounge', defaultMessage: '{isUnlimited, select, true {Unlimited} other {{count}x}} free airport lounge {count, plural, one {pass} other {passes}}', description: 'Airport lounge pass benefit in tier progression notification' },
  benefitTransfer: { id: 'LTPN9Transfer', defaultMessage: '{isUnlimited, select, true {Unlimited} other {{count}x}} free airport chauffeur service', description: 'Airport chauffeur service benefit in tier progression notification' },
  benefitInsurance: { id: 'LTPN9Insurance', defaultMessage: '{discountPercent}% travel protection discount', description: 'Travel protection discount benefit in tier progression notification' },
} as const

interface TierProgressionConfig {
  nextTier: App.LoyaltyTier
  creditsRemaining: number
  nightsRemaining: number
  expiryDate?: string
  userName?: string
  onViewProgress?: () => void
}

export function showTierProgressionNotification(config: TierProgressionConfig) {
  document.dispatchEvent(new CustomEvent<TierProgressionConfig>('tier-progression-notification', {
    detail: config,
  }))
}

export function closeTierProgressionNotification() {
  document.dispatchEvent(new CustomEvent('close-tier-progression-notification'))
}

const FloatingContainer = styled.div`
  position: absolute;
  top: calc(100% - ${rem(2)}); /* Position below the navbar user button */
  right: ${rem(-15)};
  width: ${rem(375)};
  padding: ${rem(8)};
  max-width: calc(100vw - ${rem(32)});
  z-index: ${zIndex.notification};
  transform: translateX(120%);
  opacity: 0;
  transition: transform 0.3s ${eases.power2.out}, opacity 0.3s ${eases.circ.out};

  &.is-open {
    transform: translateX(0);
    opacity: 1;
  }

  ${mediaQueryUp.desktop} {
    right: ${rem(-4)};
  }
`

const MobileFloatingContainer = styled.div`
  position: fixed;
  bottom: ${rem(16)};
  left: ${rem(16)};
  right: ${rem(16)};
  z-index: ${zIndex.notification};
  transform: translateY(calc(100% + 32px));
  opacity: 0;
  transition: transform 0.3s ${eases.power2.out}, opacity 0.3s ${eases.circ.out};

  &.is-open {
    transform: translateY(0);
    opacity: 1;
  }
`

const NotificationCard = styled.div`
  background: ${props => props.theme.palette.neutral.default.eight};
  border-radius: ${props => props.theme.borderRadius.M};
  box-shadow: ${props => props.theme.shadow.bottom.medium};
  overflow: hidden;
`

const GradientHeader = styled.div`
  padding: ${rem(16)} ${rem(20)};
  position: relative;
  ${tierGradientBackgroundStyle}
`

const HeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: ${rem(12)};
`

/**
 * Extends Heading with tier-based colors.
 * Overrides font-weight to 450 per Figma design.
 */
const HeaderTierText = styled(Heading)`
  && {
    font-weight: 450;
    display: inline;
  }
  ${tierLightContrastColorStyle}

  &.tier-platinum {
    color: ${props => props.theme.palette.product.loyalty.platinum.darkContrast};
  }
`

const HeaderDescriptionText = styled(Heading)`
  && {
    font-weight: 450;
    display: inline;
  }
  ${tierNormalContrastColorStyle}
`

/**
 * Negative margins offset the IconButton's internal padding,
 */
const StyledIconButton = styled(IconButton)`
  /* Override hover colors for platinum tier (white icon on dark background) */
  &.tier-platinum:not(:disabled) {
    &:hover {
      --button-hover-background-color: rgba(255, 255, 255, 0.1);
    }
    &:active {
      --button-active-background-color: rgba(255, 255, 255, 0.15);
    }
  }
  
  /* Other tiers (dark icon on light background) */
  &.tier-silver:not(:disabled),
  &.tier-gold:not(:disabled),
  &.tier-bronze:not(:disabled) {
    &:hover {
      --button-hover-background-color: rgba(0, 0, 0, 0.05);
    }
    &:active {
      --button-active-background-color: rgba(0, 0, 0, 0.1);
    }
  }
`

const ContentSection = styled.div`
  padding: ${rem(16)};
`

const BenefitLabel = styled(BodyText)`
  color: ${props => props.theme.palette.neutral.default.two};
`

const BenefitIconWrapper = styled.div`
  border-radius: ${props => props.theme.borderRadius.round};
  width: ${rem(24)};
  height: ${rem(24)};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  ${tierDarkBackgroundStyle}
`

const FooterActions = styled.div`
  display: flex;
  justify-content: flex-end;
`

interface TierBenefit {
  icon: React.ReactElement
  label: string
}

const BENEFIT_ICONS: Record<string, React.ReactElement> = {
  hotel_upgrade: <LineCrownIcon colour="neutral-eight" size={15} />,
  lounge_pass: <LineChairWithAirplaneIcon colour="neutral-eight" size={15} />,
  airport_transfer: <LineTaxiIcon colour="neutral-eight" size={15} />,
  discounted_insurance: <LineShieldCheckIcon colour="neutral-eight" size={15} />,
}

const TIER_BENEFIT_CODES: Partial<Record<App.LoyaltyTier, Array<App.LoyaltyBenefitType>>> = {
  silver: ['hotel_upgrade', 'lounge_pass', 'discounted_insurance'],
  gold: ['hotel_upgrade', 'lounge_pass', 'airport_transfer'],
  platinum: ['hotel_upgrade', 'lounge_pass', 'airport_transfer'],
}

function formatBenefitLabel(
  benefit: App.LoyaltyRegionTierBenefit,
  formatMessage: (descriptor: { id: string; defaultMessage: string; description?: string }, values?: Record<string, unknown>) => string,
): string | undefined {
  const countValues = {
    isUnlimited: benefit.unlimited ? 'true' : 'other',
    count: benefit.limit ?? 0,
  }

  switch (benefit.code) {
    case 'hotel_upgrade':
      return formatMessage(messages.benefitUpgrade, countValues)
    case 'lounge_pass':
      return formatMessage(messages.benefitLounge, countValues)
    case 'airport_transfer':
      return formatMessage(messages.benefitTransfer, countValues)
    case 'discounted_insurance':
      return formatMessage(messages.benefitInsurance, { discountPercent: benefit.value?.amount ?? 0 })
    default:
      return undefined
  }
}

function getTierBenefits(
  tier: App.LoyaltyTier,
  regionTierBenefits: App.LoyaltyTierRegionEnabledBenefits,
  enabledBenefits: App.LoyaltyRegionEnabledBenefits,
  formatMessage: (descriptor: { id: string; defaultMessage: string; description?: string }, values?: Record<string, unknown>) => string,
): Array<TierBenefit> {
  const codes = TIER_BENEFIT_CODES[tier] ?? []
  const benefitsLookup = new Map(
    (regionTierBenefits[tier] ?? []).map(b => [b.code, b]),
  )

  return codes.reduce<Array<TierBenefit>>((acc, code) => {
    if (!enabledBenefits[code]) return acc
    const benefit = benefitsLookup.get(code)
    if (!benefit) return acc
    const label = formatBenefitLabel(benefit, formatMessage)
    if (label) {
      acc.push({ icon: BENEFIT_ICONS[code], label })
    }
    return acc
  }, [])
}

/**
 * Renders tier progression notifications in the top-right corner
 * Relative to the navbar user button
 * Similar to AppSnackbar but for loyalty tier progression updates
 */
const AUTO_DISMISS_MS = 12000

function LoyaltyTierProgressionNotification() {
  const intl = useIntl()
  const [loyaltyConfig] = useLoyaltyConfig()
  const [currentConfig, setCurrentConfig] = useState<TierProgressionConfig>()
  const [isOpen, setIsOpen] = useState(true)
  const [isReady, setIsReady] = useState(false)
  const autoCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const autoCloseEndTimeRef = useRef<number>(0)

  // Close and reset notification state after animation completes
  const closeAndReset = useCallback(() => {
    setIsOpen(false)
    setTimeout(() => {
      setCurrentConfig(undefined)
      setIsReady(false)
    }, 300)
  }, [])

  // Listen for notification events
  useEffect(() => {
    const onNotificationEvent = (e: Event) => {
      const customEvent = e as CustomEvent<TierProgressionConfig>
      setCurrentConfig(customEvent.detail)
      setIsReady(false)
    }

    document.addEventListener('tier-progression-notification', onNotificationEvent)

    return () => {
      document.removeEventListener('tier-progression-notification', onNotificationEvent)
    }
  }, [])

  // Listen for close events (when right side menu is opened)
  useEffect(() => {
    document.addEventListener('close-tier-progression-notification', closeAndReset)

    return () => {
      document.removeEventListener('close-tier-progression-notification', closeAndReset)
    }
  }, [closeAndReset])

  const benefits = useMemo(() => {
    if (!loyaltyConfig || !currentConfig) return []
    return getTierBenefits(currentConfig.nextTier, loyaltyConfig.regionTierBenefits, loyaltyConfig.regionEnabledBenefits, intl.formatMessage)
  }, [currentConfig, loyaltyConfig, intl])

  // Only open notification once All content is ready (config + benefits loaded)
  useEffect(() => {
    if (!currentConfig) return
    if (!loyaltyConfig) return
    if (benefits.length === 0) return
    if (isReady) return

    setIsReady(true)

    // Ensure everything is rendered before opening the notification
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setIsOpen(true)
      })
    })
  }, [currentConfig, loyaltyConfig, benefits, isReady])

  const startAutoCloseTimer = useCallback(() => {
    if (autoCloseTimerRef.current) clearTimeout(autoCloseTimerRef.current)
    const endTime = Date.now() + AUTO_DISMISS_MS
    autoCloseEndTimeRef.current = endTime
    autoCloseTimerRef.current = setTimeout(closeAndReset, AUTO_DISMISS_MS)
  }, [closeAndReset])

  // Auto-dismiss after 12 seconds (pauses on hover)
  useEffect(() => {
    if (!isOpen) return
    startAutoCloseTimer()
    return () => {
      if (autoCloseTimerRef.current) {
        clearTimeout(autoCloseTimerRef.current)
        autoCloseTimerRef.current = null
      }
    }
  }, [isOpen, startAutoCloseTimer])

  const handleMouseEnter = useCallback(() => {
    if (autoCloseTimerRef.current) {
      clearTimeout(autoCloseTimerRef.current)
      autoCloseTimerRef.current = null
    }
  }, [])

  const handleMouseLeave = useCallback(() => {
    const remaining = autoCloseEndTimeRef.current - Date.now()
    if (remaining > 0) {
      autoCloseTimerRef.current = setTimeout(closeAndReset, remaining)
    } else {
      closeAndReset()
    }
  }, [closeAndReset])

  const handleViewProgress = useCallback(() => {
    currentConfig?.onViewProgress?.()
    closeAndReset()
  }, [currentConfig, closeAndReset])

  const tierName = currentConfig ? getLoyaltyTierDisplayName(currentConfig.nextTier) : ''

  const hasCredits = currentConfig && currentConfig.creditsRemaining > 0
  const hasNights = currentConfig && currentConfig.nightsRemaining > 0

  const headerIntro = currentConfig ? intl.formatMessage(messages.headerIntro, { userName: currentConfig.userName || 'you' }) : ''
  const headerStatusSuffix = intl.formatMessage(messages.headerStatusSuffix)
  const closeAriaLabel = intl.formatMessage(messages.closeAriaLabel)
  const youOnlyNeedText = intl.formatMessage(messages.youOnlyNeed)
  const toUnlockSuffix = intl.formatMessage(messages.toUnlockSuffix, { tierName })
  const byText = intl.formatMessage(messages.by)
  const statusCreditSingular = intl.formatMessage(messages.statusCreditSingular)
  const statusCreditPlural = intl.formatMessage(messages.statusCreditPlural)
  const nightSingular = intl.formatMessage(messages.nightSingular)
  const nightPlural = intl.formatMessage(messages.nightPlural)
  const andConjunction = intl.formatMessage(messages.andConjunction)
  const viewProgressText = intl.formatMessage(messages.viewProgress)

  if (!currentConfig) return null

  return (
    <>
      {/* Mobile version (0-575px) */}
      <CSSBreakpoint max="mobile">
        <MobileFloatingContainer
          className={clsx({ 'is-open': isOpen })}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <LoyaltyTierProgressionNotificationMobile
            nextTier={currentConfig.nextTier}
            creditsRemaining={currentConfig.creditsRemaining}
            nightsRemaining={currentConfig.nightsRemaining}
            userName={currentConfig.userName}
            onViewProgress={handleViewProgress}
            onClose={closeAndReset}
          />
        </MobileFloatingContainer>
      </CSSBreakpoint>

      {/* Desktop version (576px+) */}
      <CSSBreakpoint min="tablet">
        <FloatingContainer className={clsx({ 'is-open': isOpen })}>
          <NotificationCard
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <GradientHeader className={`tier-${currentConfig.nextTier}`}>
              <HeaderContent>
                <div>
                  <HeaderDescriptionText variant="heading4" className={`tier-${currentConfig.nextTier}`}>
                    {headerIntro}
                  </HeaderDescriptionText>
                  <HeaderTierText variant="heading4" className={`tier-${currentConfig.nextTier}`}>
                    {tierName}{headerStatusSuffix}
                  </HeaderTierText>
                </div>
                <StyledIconButton
                  kind="tertiary"
                  size="small"
                  onClick={closeAndReset}
                  aria-label={closeAriaLabel}
                  className={`tier-${currentConfig.nextTier}`}
                >
                  <LineTimesIcon colour={currentConfig.nextTier === 'platinum' ? 'neutral-eight' : 'neutral-one'} />
                </StyledIconButton>
              </HeaderContent>
            </GradientHeader>

            <ContentSection>
              <Group direction="vertical" gap={16}>
                <BodyText variant="medium">
                  {youOnlyNeedText}{' '}
                  <strong>
                    {hasCredits && <Plural singular={statusCreditSingular} plural={statusCreditPlural} count={currentConfig.creditsRemaining} withCount />}
                    {hasCredits && hasNights && ` ${andConjunction} `}
                    {hasNights && <Plural singular={nightSingular} plural={nightPlural} count={currentConfig.nightsRemaining} withCount />}
                  </strong>
                  {currentConfig.expiryDate && <>{byText}<FormatDateTime value={currentConfig.expiryDate} format="dmy-casual-short" /></>}
                  {' '}{toUnlockSuffix}
                </BodyText>

                <Group direction="vertical" gap={8}>
                  {benefits.map((benefit) => (
                    <Group key={benefit.label} direction="horizontal" gap={8} verticalAlign="center">
                      <BenefitIconWrapper className={`tier-${currentConfig.nextTier}`}>
                        {benefit.icon}
                      </BenefitIconWrapper>
                      <BenefitLabel variant="small">
                        {benefit.label}
                      </BenefitLabel>
                    </Group>
                  ))}

                </Group>

                <FooterActions>
                  <TextButton kind="secondary" size="small" onClick={handleViewProgress}>
                    {viewProgressText}
                  </TextButton>
                </FooterActions>
              </Group>
            </ContentSection>
          </NotificationCard>
        </FloatingContainer>
      </CSSBreakpoint>
    </>
  )
}

export default LoyaltyTierProgressionNotification
